(function () {
  const formatSpecLabel = (key) => String(key).replace(/_/g, ' ').trim();
  const normalizeSpecKey = (key) => String(key).toUpperCase().replace(/_/g, ' ').trim();
  const normalizeProductId = (id) => String(id);

  const decodeHtmlEntities = (text) => {
    if (!text || typeof text !== 'string') return '';
    return text
      .replace(/&nbsp;/gi, ' ')
      .replace(/&mdash;/gi, '—')
      .replace(/&ndash;/gi, '–')
      .replace(/&minus;/gi, '-')
      .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
      .replace(/&#x([0-9a-f]+);/gi, (_, code) => String.fromCharCode(parseInt(code, 16)));
  };

  const extractRichText = (node) => {
    if (!node) return '';
    if (typeof node === 'string') return node;
    if (node.type === 'text' && node.value) return node.value;
    if (!Array.isArray(node.children)) return '';
    return node.children.map(extractRichText).join('');
  };

  const normalizeSpecText = (value) => {
    if (value == null) return '';
    if (typeof value === 'object') {
      if (value.type === 'root') return normalizeSpecText(extractRichText(value));
      return '';
    }
    let text = decodeHtmlEntities(String(value).replace(/<[^>]*>/g, ''));
    text = text.replace(/[\u00a0\u200b\u200c\u200d\ufeff]/g, '').trim();
    text = text.replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D–—―−－]/g, '-');
    return text;
  };

  const isPlaceholderValue = (text) => {
    if (!text) return true;
    if (/^-+$/.test(text)) return true;
    return ['n/a', 'na', 'none', 'null', 'undefined', 'tbd'].includes(text.toLowerCase());
  };

  const flattenSpecValues = (value) => {
    if (value == null) return [];
    if (Array.isArray(value)) return value.flatMap(flattenSpecValues);
    if (typeof value === 'object') return [value];
    return [value];
  };

  const isEmptySpecValue = (value) => {
    const values = flattenSpecValues(value);
    if (values.length === 0) return true;
    return values.every((v) => isPlaceholderValue(normalizeSpecText(v)));
  };

  const isLabelValueSpecs = (specs) =>
    Array.isArray(specs) &&
    specs.length > 0 &&
    specs.every((item) => item && typeof item === 'object' && 'label' in item);

  const getSpecKeysFromProduct = (specs) => {
    if (!specs) return [];
    if (Array.isArray(specs)) {
      if (isLabelValueSpecs(specs)) return specs.map((item) => item.label);
      return specs.map(([key]) => key);
    }
    return Object.keys(specs);
  };

  const getSpecValues = (product, key) => {
    const specs = product?.specs;
    if (!specs) return null;
    if (Array.isArray(specs)) {
      if (isLabelValueSpecs(specs)) {
        const item = specs.find((s) => normalizeSpecKey(s.label) === normalizeSpecKey(key));
        return item ? item.value : null;
      }
      const pair = specs.find(([specKey]) => normalizeSpecKey(specKey) === normalizeSpecKey(key));
      return pair ? pair[1] : null;
    }
    if (specs[key] !== undefined) return specs[key];
    const matchedKey = Object.keys(specs).find(
      (specKey) => normalizeSpecKey(specKey) === normalizeSpecKey(key)
    );
    return matchedKey ? specs[matchedKey] : null;
  };

  const toValueArray = (value) => {
    if (value == null) return null;
    return Array.isArray(value) ? value : [value];
  };

  const buildSpecsFromProducts = (products) => {
    const specKeyMap = new Map();
    products.forEach((p) => {
      getSpecKeysFromProduct(p.specs).forEach((key) => {
        const normalized = normalizeSpecKey(key);
        if (!specKeyMap.has(normalized)) {
          specKeyMap.set(normalized, {
            key,
            label: isLabelValueSpecs(p.specs)
              ? p.specs.find((s) => normalizeSpecKey(s.label) === normalized)?.label || formatSpecLabel(key)
              : formatSpecLabel(key),
          });
        }
      });
    });
    return Array.from(specKeyMap.values());
  };

  const specHasVisibleValue = (products, key) =>
    products.some((p) => !isEmptySpecValue(getSpecValues(p, key)));

  const getSpecDisplayHtml = (value) => {
    if (typeof value === 'string') return value;
    return extractRichText(value);
  };

  const resetCompareHeights = (root) => {
    if (!root) return;
    root
      .querySelectorAll('.product-img-wrapper, .product-info > h3, .price-container, .spec-list > li')
      .forEach((el) => {
        el.style.height = '';
      });
  };

  const alignCompareHeights = (root) => {
    if (!root) return;
    const products = root.querySelectorAll('.product-list > .product-column > .product');
    if (!products.length) return;

    const alignRows = (selector) => {
      products.forEach((product) => {
        product.querySelectorAll(selector).forEach((el) => {
          el.style.height = '';
        });
      });

      const rowCount = products[0].querySelectorAll(selector).length;
      if (!rowCount) return;

      for (let i = 0; i < rowCount; i++) {
        let maxHeight = 0;
        const rowItems = [];

        products.forEach((product) => {
          const item = product.querySelectorAll(selector)[i];
          if (!item) return;
          const height = item.getBoundingClientRect().height;
          if (height > maxHeight) maxHeight = height;
          rowItems.push(item);
        });

        if (maxHeight > 0) {
          rowItems.forEach((item) => {
            item.style.height = `${maxHeight}px`;
          });
        }
      }
    };

    alignRows('.product-img-wrapper');
    alignRows('.product-info > h3');
    alignRows('.price-container');
    alignRows('.spec-list > li');
  };

  let alignFrameId = 0;
  const scheduleCompareAlign = (root) => {
    cancelAnimationFrame(alignFrameId);
    alignFrameId = requestAnimationFrame(() => {
      alignFrameId = requestAnimationFrame(() => {
        alignCompareHeights(root);
      });
    });
  };

  const debounce = (fn, delay) => {
    let timer = null;
    const debounced = function (...args) {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        fn.apply(this, args);
      }, delay);
    };
    debounced.cancel = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };
    return debounced;
  };

  document.addEventListener('alpine:init', () => {
    Alpine.data('productCompareApp', (config) => ({
      currentProduct: config.currentProduct || null,
      collectionProducts: config.collectionProducts || [],
      slotProductIds: config.defaultSlotIds || [],
      currentProductId: config.currentProductId,
      currentHandle: config.currentHandle || '',
      shopNowLabel: config.shopNowLabel || 'Shop now',
      currentProductLabel: config.currentProductLabel || 'Current product',
      selectedLabel: config.selectedLabel || '(selected)',
      sectionId: config.sectionId,
      _alignDebounced: null,

      init() {
        const root = document.getElementById(`shopify-section-${this.sectionId}`);
        this._alignDebounced = debounce(() => scheduleCompareAlign(root), 150);

        this.$watch('layoutKey', () => {
          resetCompareHeights(root);
          scheduleCompareAlign(root);
          setTimeout(() => scheduleCompareAlign(root), 150);
          setTimeout(() => scheduleCompareAlign(root), 450);
        });

        root?.addEventListener(
          'load',
          (event) => {
            if (event.target.matches?.('.product-img')) this._alignDebounced();
          },
          true
        );

        window.addEventListener('resize', this._alignDebounced);
      },

      getProductById(id) {
        const normalizedId = normalizeProductId(id);
        if (normalizeProductId(this.currentProductId) === normalizedId) {
          return this.currentProduct;
        }
        return this.collectionProducts.find(
          (product) => normalizeProductId(product.id) === normalizedId
        );
      },

      get selectedProducts() {
        return this.slotProductIds.map((id) => this.getProductById(id)).filter(Boolean);
      },

      get availableSpecs() {
        return buildSpecsFromProducts(this.selectedProducts).filter((s) =>
          specHasVisibleValue(this.selectedProducts, s.key)
        );
      },

      get visibleSpecs() {
        return this.availableSpecs;
      },

      get layoutKey() {
        return `${this.slotProductIds.join('|')}|${this.visibleSpecs.length}`;
      },

      get productCountClass() {
        return 'is-count-3';
      },

      isCurrentProduct(product) {
        return Boolean(product?.handle && product.handle === this.currentHandle);
      },

      isSlotCurrent(index) {
        return index === 0;
      },

      getSelectOptions(slotIndex) {
        if (slotIndex === 0 && this.currentProduct) {
          return [
            {
              id: this.currentProduct.id,
              label: `${this.currentProduct.name} ${this.selectedLabel}`.trim(),
              disabled: false,
            },
          ];
        }

        const options = [];
        const currentId = normalizeProductId(this.currentProductId);

        this.collectionProducts.forEach((product) => {
          if (normalizeProductId(product.id) === currentId) return;

          const isSelectedElsewhere = this.slotProductIds.some(
            (slotId, index) =>
              index !== slotIndex && normalizeProductId(slotId) === normalizeProductId(product.id)
          );

          if (isSelectedElsewhere) return;

          options.push({
            id: product.id,
            label: product.name,
            disabled: false,
          });
        });

        return options;
      },

      changeSlotProduct(slotIndex, newProductId) {
        const normalizedNewId = normalizeProductId(newProductId);
        const currentSlotId = normalizeProductId(this.slotProductIds[slotIndex]);

        if (normalizedNewId === currentSlotId) return;

        const isAlreadySelected = this.slotProductIds.some(
          (slotId, index) => index !== slotIndex && normalizeProductId(slotId) === normalizedNewId
        );

        if (isAlreadySelected) return;

        const nextIds = [...this.slotProductIds];
        nextIds[slotIndex] = Number(newProductId);
        this.slotProductIds = nextIds;
      },

      isSlotOptionSelected(slotIndex, optionId) {
        return (
          normalizeProductId(this.slotProductIds[slotIndex]) === normalizeProductId(optionId)
        );
      },

      getSpecValues(product, key) {
        return getSpecValues(product, key);
      },

      toValueArray(value) {
        return toValueArray(value);
      },

      isEmptySpecValue(value) {
        return isEmptySpecValue(value);
      },

      getSpecDisplayHtml(value) {
        return getSpecDisplayHtml(value);
      },

      hasComparePrice(product) {
        return product.original_compare_price > product.original_price;
      },
    }));
  });
})();
